/**
 * Éditeur du contenu public d'un voyage : programme jour par jour, hôtels, notes importantes.
 * Utilisé dans le formulaire « Modifier le voyage » (AdminDashboard).
 */
import React from 'react';
import { REPAS_OPTIONS } from '../../utils/repas';
import axios from 'axios';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const inputCls = 'w-full px-2 py-1.5 border rounded text-xs';

const dateFr = (iso, offsetDays) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + offsetDays);
  return `${d.getDate()} ${MOIS[d.getMonth()]}`;
};

export const recalerDatesProgramme = (itinerary, startDate) => {
  if (!startDate) return itinerary;
  return itinerary.map((day, i) => ({ ...day, day: i + 1, date: dateFr(startDate, i) }));
};

const Section = ({ title, hint, action, children }) => (
  <div className="md:col-span-2 border-t pt-3">
    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
      <div>
        <p className="text-sm font-bold text-[#1E3A5F]">{title}</p>
        {hint && <p className="text-[11px] text-gray-500">{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

export const ItineraryEditor = ({ itinerary, startDate, onChange, onImportClick }) => {
  const days = itinerary || [];
  const set = (i, patch) => onChange(days.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const remove = (i) => onChange(days.filter((_, j) => j !== i).map((d, j) => ({ ...d, day: j + 1 })));
  const add = () => onChange([...days, { day: days.length + 1, date: startDate ? dateFr(startDate, days.length) : '', title: '', events: [] }]);
  return (
    <Section title="📅 Programme jour par jour" hint="Affiché sur la page publique du voyage. Une étape par ligne."
      action={<div className="flex gap-2 flex-wrap">
        {onImportClick && (
          <button type="button" onClick={onImportClick} className="text-[11px] bg-emerald-600 text-white px-2 py-1 rounded font-semibold hover:bg-emerald-700" data-testid="itinerary-import-btn"
            title="Coller le programme du fournisseur (texte ou PDF) : l'IA remplit les jours">📄 Importer un programme (texte / PDF)</button>
        )}
        {startDate && days.length > 0 && (
          <button type="button" onClick={() => onChange(recalerDatesProgramme(days, startDate))}
            className="text-[11px] bg-amber-100 text-amber-800 px-2 py-1 rounded font-semibold hover:bg-amber-200" data-testid="itinerary-recaler-btn"
            title="Recalcule la date de chaque jour à partir de la date de départ">🔁 Recaler les dates sur le départ</button>
        )}
        <button type="button" onClick={add} className="text-[11px] bg-[#1E3A5F] text-white px-2 py-1 rounded font-semibold" data-testid="itinerary-add-day-btn">+ Jour</button>
      </div>}>
      {days.length === 0 && <p className="text-xs text-gray-400 italic">Aucun jour — clique « + Jour » pour démarrer le programme.</p>}
      <div className="space-y-2">
        {days.map((d, i) => (
          <div key={i} className="bg-gray-50 border rounded-lg p-2 grid grid-cols-1 md:grid-cols-12 gap-2" data-testid={`itinerary-day-row-${i}`}>
            <div className="md:col-span-1 flex items-center justify-center font-bold text-[#5BC5D4] text-sm">J{i + 1}</div>
            <input className={`${inputCls} md:col-span-2`} placeholder="Date (ex : 9 novembre)" value={d.date || ''} onChange={e => set(i, { date: e.target.value })} data-testid={`itinerary-date-${i}`} />
            <input className={`${inputCls} md:col-span-3`} placeholder="Titre du jour" value={d.title || ''} onChange={e => set(i, { title: e.target.value })} data-testid={`itinerary-title-${i}`} />
            <textarea className={`${inputCls} md:col-span-5`} rows={2} placeholder="Étapes (1 par ligne)"
              value={(d.events || d.activities || []).join('\n')}
              onChange={e => set(i, { events: e.target.value.split('\n'), activities: undefined })}
              onBlur={e => set(i, { events: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
              data-testid={`itinerary-events-${i}`} />
            <button type="button" onClick={() => remove(i)} className="md:col-span-1 text-red-500 text-xs font-bold hover:text-red-700" data-testid={`itinerary-remove-${i}`}>✕</button>
          </div>
        ))}
      </div>
    </Section>
  );
};

export const HotelsEditor = ({ hotels, onChange }) => {
  const list = hotels || [];
  const set = (i, patch) => onChange(list.map((h, j) => (j === i ? { ...h, ...patch } : h)));
  return (
    <Section title="🏨 Hébergement" hint="Nom, ville, nuits, étoiles, formule repas par étape (si le voyage est « Formules différentes selon les étapes »)."
      action={<button type="button" onClick={() => onChange([...list, { name: '', location: '', nights: null, stars: null }])}
        className="text-[11px] bg-[#1E3A5F] text-white px-2 py-1 rounded font-semibold" data-testid="hotels-add-btn">+ Hôtel</button>}>
      <div className="space-y-2">
        {list.map((h, i) => (
          <div key={i} className="grid grid-cols-2 md:grid-cols-12 gap-2 items-center" data-testid={`hotel-row-${i}`}>
            <input className={`${inputCls} md:col-span-5`} placeholder="Nom de l'hôtel" value={h.name || ''} onChange={e => set(i, { name: e.target.value })} data-testid={`hotel-name-${i}`} />
            <input className={`${inputCls} md:col-span-3`} placeholder="Ville" value={h.location || ''} onChange={e => set(i, { location: e.target.value })} />
            <input type="number" className={`${inputCls} md:col-span-1`} placeholder="Nuits" value={h.nights ?? ''} onChange={e => set(i, { nights: e.target.value === '' ? null : parseInt(e.target.value, 10) })} />
            <input type="number" className={`${inputCls} md:col-span-1`} placeholder="★" value={h.stars ?? ''} onChange={e => set(i, { stars: e.target.value === '' ? null : parseInt(e.target.value, 10) })} />
            <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="md:col-span-1 text-red-500 text-xs font-bold" data-testid={`hotel-remove-${i}`}>✕</button>
            <select className={`${inputCls} md:col-span-12`} value={h.meal_plan || ''} onChange={e => set(i, { meal_plan: e.target.value || null })} data-testid={`hotel-meal-${i}`}>
              <option value="">🍴 Formule repas de cet hébergement — (idem voyage)</option>
              {REPAS_OPTIONS.filter(o => o.value !== 'par_etape').map(o => <option key={o.value} value={o.value}>🍴 {o.label}</option>)}
            </select>
          </div>
        ))}
      </div>
    </Section>
  );
};

export const NotesEditor = ({ notes, onChange }) => (
  <Section title="⚠️ Notes importantes" hint="Une note par ligne (cotisation, assurance, programme modifiable…).">
    <textarea className="w-full px-3 py-2 border rounded-lg text-sm" rows={4} value={(notes || []).join('\n')}
      onChange={e => onChange(e.target.value.split('\n'))}
      onBlur={e => onChange(e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
      data-testid="trip-form-important-notes" />
  </Section>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProgrammeImportModal = ({ open, onClose, startDate, getAuthConfig, onApply }) => {
  const [texte, setTexte] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [result, setResult] = React.useState(null);
  const [choix, setChoix] = React.useState({ itinerary: true, hotels: true, highlights: false, included: false, not_included: false, important_notes: false });
  if (!open) return null;

  const analyser = async () => {
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('texte', texte); fd.append('start_date', startDate || '');
      if (file) fd.append('file', file);
      const r = await axios.post(`${API}/admin/trips/parse-programme`, fd, { ...getAuthConfig(), timeout: 120000 });
      setResult(r.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const appliquer = () => {
    const out = {};
    Object.keys(choix).forEach(k => { if (choix[k] && result[k]?.length) out[k] = result[k]; });
    onApply(out); onClose();
  };
  const Opt = ({ k, label }) => result[k]?.length > 0 && (
    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={choix[k]} onChange={e => setChoix({ ...choix, [k]: e.target.checked })} data-testid={`prog-import-choix-${k}`} />{label} <span className="text-gray-400">({result[k].length})</span></label>
  );

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/60" onClick={onClose} data-testid="programme-import-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#1E3A5F]">📄 Importer un programme fournisseur</h3>
          <button type="button" onClick={onClose} className="text-xl font-bold text-gray-400" data-testid="prog-import-close">×</button>
        </div>
        {!result ? (
          <>
            <p className="text-xs text-gray-500">Colle le texte du programme (mail, brochure, Word…) et/ou joins le PDF : l'IA le découpe en jours, hôtels, points forts, inclus / non inclus.</p>
            <textarea value={texte} onChange={e => setTexte(e.target.value)} rows={10} className="w-full border rounded-lg p-2 text-xs" placeholder={"Jour 1 : Lyon - Lisbonne…\nJour 2 : Sintra et Cascais…"} data-testid="prog-import-texte" />
            <div className="flex items-center gap-2 text-xs">
              <input type="file" accept="application/pdf,.txt" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="prog-import-file" />
              {file && <span className="text-gray-500">{file.name}</span>}
            </div>
            {error && <p className="text-xs text-red-600 bg-red-50 rounded p-2" data-testid="prog-import-error">{error}</p>}
            <div className="flex justify-end">
              <button type="button" onClick={analyser} disabled={busy || (!texte.trim() && !file)} className="bg-[#1E3A5F] text-white px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-50" data-testid="prog-import-analyser">{busy ? '⏳ Analyse en cours…' : '🔍 Analyser'}</button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900" data-testid="prog-import-result">
              <p className="font-bold">✓ {result.nb_jours} jour{result.nb_jours > 1 ? 's' : ''} détecté{result.nb_jours > 1 ? 's' : ''}</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                {result.itinerary.map(d => <li key={d.day}><strong>J{d.day}</strong> {d.date && <span className="text-emerald-700">{d.date}</span>} — {d.title} <span className="text-gray-500">({d.events.length} étapes)</span></li>)}
              </ul>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-bold text-[#1E3A5F]">Remplacer dans la fiche :</p>
              <Opt k="itinerary" label="Programme jour par jour" />
              <Opt k="hotels" label="Hébergement" />
              <Opt k="highlights" label="Points forts" />
              <Opt k="included" label="Inclus" />
              <Opt k="not_included" label="Non inclus" />
              <Opt k="important_notes" label="Notes importantes" />
            </div>
            <p className="text-[11px] text-gray-500">Tu pourras tout retoucher ensuite dans le formulaire avant d'enregistrer.</p>
            <div className="flex justify-between">
              <button type="button" onClick={() => setResult(null)} className="text-sm text-gray-500" data-testid="prog-import-retour">← Modifier le texte</button>
              <button type="button" onClick={appliquer} className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold" data-testid="prog-import-appliquer">✅ Appliquer à la fiche</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
